import React, { useState, useRef, useEffect } from 'react';
import { styles } from '../styles';
import { Icons } from './Icons';

/**
 * AddPatientForm Component
 * Form to add new patient with procedure selection
 *
 * Props:
 * - procedures: [{ id, name, duration_minutes }] from API
 * - onSubmit: ({ name, procedures }) => void
 * - onClose: () => void
 */
function AddPatientForm({ procedures, onSubmit, onClose }) {
  const [name, setName] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [startTime, setStartTime] = useState(() => {
    const now = new Date();
    return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
  });
  const nameRef = useRef(null);

  // Auto-focus name input on mount
  useEffect(() => {
    const timer = setTimeout(() => nameRef.current?.focus(), 100);
    return () => clearTimeout(timer);
  }, []);

  // Toggle procedure selection
  const toggle = (id) => {
    setSelectedIds(prev =>
      prev.includes(id)
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      // Don't handle hotkeys when typing in name input
      if (document.activeElement === nameRef.current) {
        // Allow Enter to blur and move to procedure selection
        if (e.key === 'Enter') {
          e.preventDefault();
          nameRef.current.blur();
        }
        return;
      }

      // Number keys 1-8 toggle procedures
      const num = parseInt(e.key);
      if (num >= 1 && num <= Math.min(8, procedures.length)) {
        e.preventDefault();
        toggle(procedures[num - 1].id);
      }

      // Enter to submit
      if (e.key === 'Enter') {
        e.preventDefault();
        submit();
      }

      // Escape to close
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  // Submit form
  const submit = () => {
    if (!name.trim()) {
      nameRef.current?.focus();
      return;
    }

    const selected = procedures.filter(p => selectedIds.includes(p.id));
    if (selected.length === 0) return;

    onSubmit({
      name: name.trim(),
      procedures: selected,
      startTime: startTime,
    });

    // Reset form
    setName('');
    setSelectedIds([]);
  };

  // Calculate total time
  const totalTime = procedures
    .filter(p => selectedIds.includes(p.id))
    .reduce((sum, p) => sum + (p.duration_minutes || p.duration || 0), 0);

  // Check if form is valid
  const isValid = name.trim() && selectedIds.length > 0;

  return (
    <div style={styles.formWrap}>
      {/* Patient Name Input */}
      <div style={styles.formSection}>
        <label style={styles.formLabel}>Tên bệnh nhân</label>
        <input
          ref={nameRef}
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.target.blur();
            }
          }}
          placeholder="Nhập tên rồi nhấn Tab..."
          style={styles.formInput}
        />
      </div>

      {/* Start Time Input */}
      <div style={styles.formSection}>
        <label style={styles.formLabel}>Giờ bắt đầu</label>
        <input
          type="time"
          value={startTime}
          onChange={e => setStartTime(e.target.value)}
          style={{...styles.formInput, width: 200, fontSize: 24, padding: '12px 18px'}}
        />
      </div>

      {/* Procedure Selection Grid */}
      <div style={styles.formSection}>
        <label style={styles.formLabel}>
          {Icons.zap({ size: 12, color: '#f59e0b' })}
          <span>Thủ thuật</span>
          <span style={styles.formLabelHint}>
            Phím 1–{Math.min(8, procedures.length)}
          </span>
        </label>
        <div style={styles.procGrid}>
          {procedures.map((proc, idx) => {
            const active = selectedIds.includes(proc.id);
            const duration = proc.duration_minutes || proc.duration || 0;

            return (
              <button
                key={proc.id}
                onClick={() => toggle(proc.id)}
                style={active ? styles.procBtnActive : styles.procBtn}
              >
                <span style={styles.procName}>{proc.name}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={styles.procDur}>{duration}p</span>
                  {idx < 8 && (
                    <span style={active ? styles.hotkeyActive : styles.hotkey}>
                      {idx + 1}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Footer: Selected info + Submit button */}
      <div style={styles.formFooter}>
        <div style={styles.formFooterInfo}>
          {selectedIds.length > 0 && (
            <span style={styles.selectedInfo}>
              {selectedIds.length} thủ thuật • ~{totalTime} phút
            </span>
          )}
        </div>
        <button
          onClick={submit}
          disabled={!isValid}
          style={{
            ...styles.btnSubmit,
            opacity: isValid ? 1 : 0.4,
            cursor: isValid ? 'pointer' : 'default',
          }}
        >
          Thêm vào lịch
        </button>
      </div>
    </div>
  );
}

export default AddPatientForm;
